import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #mobileSingleQuery = window.matchMedia('(max-width: 479px)');

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#mobileSingleQuery.addEventListener('change', this.#handleForcedMobileSingleChange);
    this.setAttribute('initialized', '');
    this.#syncForcedMobileSingle();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#mobileSingleQuery.removeEventListener('change', this.#handleForcedMobileSingleChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    if (this.#mobileSingleQuery.matches && target.name === 'grid-mobile') {
      sessionStorage.setItem('product-grid-view-mobile', target.value);
      this.#syncForcedMobileSingle();
      this.#closeFiltersDrawer(target);
      return;
    }

    this.#animateLayoutChange(target.value);

    if (target.name === 'grid-mobile' && !mediaQueryLarge.matches) {
      this.#closeFiltersDrawer(target);
    }
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #handleForcedMobileSingleChange = () => {
    if (this.#mobileSingleQuery.matches) {
      this.#syncForcedMobileSingle();
      return;
    }

    if (mediaQueryLarge.matches) return;

    const storedLayout = sessionStorage.getItem('product-grid-view-mobile') || 'default';
    const targetElement =
      this.querySelector(`input[type="radio"][name="grid-mobile"][value="${storedLayout}"]`) ??
      this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout(storedLayout);
  };

  #syncForcedMobileSingle() {
    if (!this.#mobileSingleQuery.matches || mediaQueryLarge.matches) return;

    const targetElement = this.querySelector('input[type="radio"][name="grid-mobile"][value="mobile-single"]');

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout('mobile-single');
  }

  #closeFiltersDrawer(target) {
    const filtersDrawer = target.closest('#filters-drawer');

    if (!(filtersDrawer instanceof HTMLElement) || typeof filtersDrawer.closeDialog !== 'function') return;

    filtersDrawer.closeDialog();
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
